import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";
import Header from "components/Headers/Header.js";
import { taskmanageService } from "../services/taskmanageService";

/* ===== file-type icon (Finder/Explorer style) ===== */
const fileIcon = (name = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  const map = {
    pdf: ["fa-file-pdf", "#e23c3c"],
    xls: ["fa-file-excel", "#1d7044"],
    xlsx: ["fa-file-excel", "#1d7044"],
    csv: ["fa-file-csv", "#1d7044"],
    doc: ["fa-file-word", "#2b579a"],
    docx: ["fa-file-word", "#2b579a"],
    png: ["fa-file-image", "#8e44ad"],
    jpg: ["fa-file-image", "#8e44ad"],
    jpeg: ["fa-file-image", "#8e44ad"],
    gif: ["fa-file-image", "#8e44ad"],
    html: ["fa-file-code", "#e67e22"],
    htm: ["fa-file-code", "#e67e22"],
    xml: ["fa-file-code", "#e67e22"],
    json: ["fa-file-code", "#e67e22"],
    zip: ["fa-file-archive", "#7f8c8d"],
    rar: ["fa-file-archive", "#7f8c8d"],
    txt: ["fa-file-alt", "#7f8c8d"],
    eml: ["fa-envelope", "#5e72e4"],
    msg: ["fa-envelope", "#5e72e4"],
  };
  const [icon, color] = map[ext] || ["fa-file", "#95a5a6"];
  return { icon, color };
};

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(1)} ${units[i]}`;
};

const DocumentBrowser = () => {
  const [browser, setBrowser] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFolder = (prefix = "") => {
    setLoading(true);
    taskmanageService
      .s3BucketBrowse(prefix)
      .then((response) => {
        setBrowser(response && response.data ? response.data : null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFolder("");
  }, []);

  const openFile = (f) => {
    // Open the presigned URL directly: the browser previews PDFs/images inline
    // and downloads Office files (which then open in desktop Excel/Word/etc.).
    window.open(f.url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <div className="mt-4" style={{ position: "relative", zIndex: 10 }}>
          <div className="d-flex align-items-center mb-3">
            <h3 className="mb-0">Documents</h3>
            <button
              className="btn btn-link btn-sm ml-2 p-0"
              onClick={() => loadFolder(browser ? browser.prefix : "")}
              title="Refresh"
            >
              <i className="fas fa-sync"></i>
            </button>
          </div>

          {loading ? (
            <p style={{ color: "#5d6cae" }}>
              <i className="fas fa-spinner fa-spin"></i> Loading folder…
            </p>
          ) : !browser ? (
            <p className="text-muted">Could not load the document bucket.</p>
          ) : (
            <>
              {/* Breadcrumbs */}
              <div className="mb-3">
                {(browser.breadcrumbs || []).map((crumb, idx) => (
                  <span key={crumb.prefix + idx}>
                    {idx > 0 && <span className="mx-1 text-muted">/</span>}
                    <a
                      style={{ cursor: "pointer", color: "#5d6cae" }}
                      onClick={() => loadFolder(crumb.prefix)}
                    >
                      {crumb.name}
                    </a>
                  </span>
                ))}
              </div>

              {!(browser.directories || []).length &&
              !(browser.files || []).length ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-folder-open fa-3x mb-2 d-block" style={{ opacity: 0.4 }}></i>
                  This folder is empty.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {(browser.directories || []).map((dir) => (
                    <div
                      key={dir.prefix}
                      onClick={() => loadFolder(dir.prefix)}
                      title={dir.name}
                      style={{
                        cursor: "pointer",
                        textAlign: "center",
                        padding: "16px 6px",
                        borderRadius: "8px",
                        border: "1px solid #eef0f5",
                        background: "#fafbfe",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#eef1fb")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fafbfe")}
                    >
                      <i
                        className="fas fa-folder d-block"
                        style={{ fontSize: "46px", color: "#f6c244", marginBottom: "8px" }}
                      ></i>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#32325d",
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {dir.name}
                      </div>
                    </div>
                  ))}

                  {(browser.files || []).map((f) => {
                    const { icon, color } = fileIcon(f.name);
                    return (
                      <div
                        key={f.key}
                        onClick={() => openFile(f)}
                        title={f.name}
                        style={{
                          cursor: "pointer",
                          textAlign: "center",
                          padding: "16px 6px",
                          borderRadius: "8px",
                          border: "1px solid #eef0f5",
                          background: "#fff",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f6f7fb")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <i
                          className={`fas ${icon} d-block`}
                          style={{ fontSize: "46px", color, marginBottom: "8px" }}
                        ></i>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#32325d",
                            wordBreak: "break-word",
                            lineHeight: "1.2",
                          }}
                        >
                          {f.name}
                        </div>
                        <div style={{ fontSize: "11px", color: "#8898aa", marginTop: "2px" }}>
                          {formatBytes(f.size)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </>
  );
};

export default DocumentBrowser;
